import { useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Flag, AlertTriangle, Clock, ChevronRight, Calendar, Target } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { SectionHelp } from "@/components/help";

interface Milestone {
  id: number;
  name: string;
  projectName: string;
  targetDate: string;
  status: string;
  daysUntil: number;
  isOverdue: boolean;
  isAtRisk: boolean;
}

interface MilestonesWidgetProps {
  milestones: Milestone[];
}

export default function MilestonesWidget({ milestones }: MilestonesWidgetProps) {
  const [, navigate] = useLocation();

  if (milestones.length === 0) {
    return (
      <Card className="h-full border-0 shadow-lg bg-card">
        <CardHeader className="pb-3 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 shadow-md">
              <Flag className="h-5 w-5 text-white" />
            </div>
            <SectionHelp contentId="dashboard:milestones" tooltip="Learn about milestones">
              <CardTitle className="text-lg font-bold">Milestones</CardTitle>
            </SectionHelp>
          </div>
        </CardHeader>
        <CardContent className="p-5">
          <div className="py-8 text-center">
            <div className="w-12 h-12 bg-muted rounded-xl flex items-center justify-center mx-auto mb-3">
              <Calendar className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground font-medium text-sm">No upcoming milestones</p>
            <p className="text-xs text-muted-foreground mt-1">All caught up!</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full border-0 shadow-lg bg-card">
      <CardHeader className="pb-3 border-b border-border flex flex-row items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 shadow-md">
            <Flag className="h-5 w-5 text-white" />
          </div>
          <CardTitle className="text-lg font-bold">Milestones</CardTitle>
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          className="text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/30 gap-1 font-semibold h-8 px-2"
          onClick={() => navigate("/projects")}
        >
          View <ChevronRight className="h-3 w-3" />
        </Button>
      </CardHeader>
      <CardContent className="p-4">
        <div className="space-y-3">
          {milestones.slice(0, 4).map((m) => (
            <div 
              key={m.id}
              className={cn(
                "flex items-center gap-3 p-3 rounded-xl border transition-all duration-200 cursor-pointer",
                m.isOverdue 
                  ? "bg-red-50 border-red-200 hover:border-red-300 dark:bg-red-950/30 dark:border-red-800 dark:hover:border-red-700"
                  : m.isAtRisk 
                    ? "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800 hover:border-amber-300 dark:hover:border-amber-700" 
                    : "bg-card border-border hover:border-border hover:shadow-sm"
              )}
            >
              <div className={cn(
                "flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center",
                m.isOverdue 
                  ? "bg-red-100 dark:bg-red-900/30" 
                  : m.isAtRisk 
                    ? "bg-amber-100 dark:bg-amber-900/30" 
                    : "bg-muted"
              )}>
                {m.isOverdue ? (
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                ) : m.isAtRisk ? (
                  <Clock className="h-5 w-5 text-amber-500" />
                ) : (
                  <Target className="h-5 w-5 text-muted-foreground" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-foreground text-sm truncate">{m.name}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 truncate">{m.projectName}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <div className={cn(
                  "text-xs font-bold px-2 py-1 rounded-md",
                  m.isOverdue ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300" : 
                  m.isAtRisk ? "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300" : 
                  "bg-muted text-muted-foreground"
                )}>
                  {m.isOverdue ? `${Math.abs(m.daysUntil)}d late` : 
                   m.daysUntil === 0 ? "Today" :
                   m.daysUntil === 1 ? "Tomorrow" :
                   `${m.daysUntil}d`}
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
